import {SegmentedControl} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React, {useCallback} from 'react';

import {assert} from 'Asserts';
import {GetPipelineConfigSpecType} from 'pipelines/StepShapeUtils';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import {IconSelectorFieldSettings} from './StepConfigEditorTypes';

type IconSelectorValue = string | number | null;
export interface IconEnumSelectorProps {
  disabled?: boolean;
  fieldSettings: IconSelectorFieldSettings;
  configShape: GetPipelineConfigSpecType<'enum'>;
  selectedOption: IconSelectorValue;
  onChangeConfig: (option: IconSelectorValue) => void;
  isExpandable?: boolean;
  isExpanded?: boolean;
}

export default function IconEnumSelector({
  disabled = false,
  fieldSettings,
  configShape,
  selectedOption,
  onChangeConfig,
  isExpandable,
  isExpanded,
}: IconEnumSelectorProps): JSX.Element {
  const options = configShape.choices.map((choice) => ({
    value: String(choice),
    label: (
      <Tooltip isDisabled={!fieldSettings.displayNames?.[choice]} text={fieldSettings.displayNames?.[choice]}>
        <div className={css(styles.segmentedControlItem)}>
          <BenchlingIcon icon={fieldSettings.iconNames[choice]} />
        </div>
      </Tooltip>
    ),
  }));

  const handleChange = useCallback(
    (value: string): void => {
      const matches = configShape.choices.filter((choice) => String(choice) === value);
      assert(
        matches.length > 0,
        `No match for value "${value}" in choices: ${JSON.stringify(configShape.choices)}`
      );

      assert(
        matches.length === 1,
        `Ambiguous match for value "${value}" in choices: ${JSON.stringify(configShape.choices)}`
      );
      const matchedChoice = matches[0];

      onChangeConfig(matchedChoice);
    },
    [configShape.choices, onChangeConfig]
  );

  return (
    <div className={css(styles.blockDisplay)}>
      <SegmentedControl
        data={options}
        value={String(selectedOption)}
        onChange={handleChange}
        disabled={disabled}
        fullWidth={true}
        classNames={{
          label: css(
            styles.segmentedControlLabel,
            isExpandable && expandableStyles.rightRadius,
            isExpanded && expandableStyles.bottomLeftRadius
          ),
          control: css(
            isExpandable && expandableStyles.rightRadius,
            isExpanded && expandableStyles.bottomLeftRadius
          ),
          root: css(
            isExpandable && expandableStyles.rightRadius,
            isExpanded && expandableStyles.bottomLeftRadius
          ),
        }}
      />
    </div>
  );
}

const expandableStyles = StyleSheet.create({
  rightRadius: {
    borderBottomRightRadius: 0,
    borderTopRightRadius: 0,
  },
  bottomLeftRadius: {
    borderBottomLeftRadius: 0,
  },
});

const styles = StyleSheet.create({
  segmentedControlLabel: {
    padding: 0,
  },
  segmentedControlItem: {
    padding: '10px 0',
  },
  blockDisplay: {
    width: '100%',
  },
  expanded: {
    marginBottom: '-16px',
  },
});
